import WishlistAction from '@c/toasts/WishlistAction';
import {
  addProductToFavorites,
  removeProductFromFavorites,
} from '@util/firestore/products';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { motion, useAnimation } from 'framer-motion';

interface HeartIconButtonProps {
  itemId: string;
  initialCount?: number;
  hideCount?: boolean;
}

// Clean, symmetrical heart SVG with better mobile visibility
const HeartSvg = ({ filled }: { filled: boolean }) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="w-[18px] h-[18px] sm:w-[22px] sm:h-[22px]"
  >
    <path
      d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
      fill={filled ? '#E53935' : 'none'}
      stroke={filled ? '#E53935' : '#4B5563'}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const HeartIconButton = ({
  itemId,
  initialCount = 0,
  hideCount = false,
}: HeartIconButtonProps) => {
  const [isLiked, setLiked] = useState(false);
  const [favoriteCount, setFavoriteCount] = useState(initialCount);
  const controls = useAnimation();

  const { userDoc } = useAuth();
  const queryClient = useQueryClient();
  const { showToast } = useToastContext();

  useEffect(() => {
    if (userDoc) {
      const liked = userDoc.favorites?.includes(itemId);
      setLiked(!!liked);
    }
  }, [itemId, userDoc]);

  const onButtonClick = () => {
    // Trigger pop animation (non-blocking)
    controls.start({
      scale: [1, 1.2, 1],
      transition: { duration: 0.25, ease: 'easeOut' },
    });

    // Update state immediately
    const newLikedState = !isLiked;
    setLiked(newLikedState);

    if (userDoc) {
      if (isLiked) {
        removeProductFromFavorites(userDoc.uid, itemId);
        setFavoriteCount(favoriteCount - 1);
      } else {
        addProductToFavorites(userDoc.uid, itemId);
        setFavoriteCount(favoriteCount + 1);
      }
      queryClient.invalidateQueries({
        queryKey: ['user'],
      });
      showToast(<WishlistAction removed={isLiked} />);
    } else {
      window.open(
        '/login',
        'Login/Register',
        'width=600,height=700,top=0,left=0'
      );
    }
  };

  return (
    <motion.button
      className="flex h-[2.4rem] w-[2.4rem] sm:h-[3.2rem] sm:w-[3.2rem]
        items-center justify-center
        select-none rounded-full
        bg-white/40 shadow-xs
        hover:bg-white/70 hover:shadow-md
        transition-all duration-200"
      onClick={onButtonClick}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      animate={controls}
    >
      <motion.div
        initial={false}
        animate={isLiked ? { scale: [1, 1.2, 1] } : { scale: 1 }}
        transition={{ duration: 0.2 }}
      >
        <HeartSvg filled={isLiked} />
      </motion.div>
    </motion.button>
  );
};

export default HeartIconButton;
